import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Table,
  RefreshCw,
  Search,
  Plus,
  Download,
  Upload,
  Filter,
  Save,
  X,
  Check,
  ChevronDown,
  Loader2,
  Trash2,
  Edit3,
  Copy,
  MoreHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  User,
  FileSpreadsheet,
  Settings,
  Columns,
  GripVertical,
  PlusCircle,
  Pencil,
  FolderPlus,
  UserPlus,
  Maximize2,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { SpreadsheetGrid } from '../components/sheets/SpreadsheetGrid';
import { FreeSheetGrid } from '../components/sheets/FreeSheetGrid';
import { SheetCsvImport } from '../components/sheets/SheetCsvImport';
import Papa from 'papaparse';

const API = process.env.REACT_APP_BACKEND_URL;

// Define all available columns
const ALL_COLUMNS = [
  { id: 'candidate_name', label: 'Name', width: 180, type: 'text', required: true },
  { id: 'email', label: 'Email', width: 220, type: 'email', required: true },
  { id: 'phone', label: 'Phone', width: 140, type: 'phone' },
  { id: 'current_company', label: 'Company', width: 160, type: 'text' },
  { id: 'current_designation', label: 'Designation', width: 160, type: 'text' },
  { id: 'experience', label: 'Experience', width: 100, type: 'text' },
  { id: 'current_location', label: 'Location', width: 140, type: 'text' },
  { id: 'skills', label: 'Skills', width: 200, type: 'text' },
  { id: 'status', label: 'Status', width: 130, type: 'select', options: ['Active', 'Inactive', 'Hired', 'Rejected', 'On Hold'] },
  { id: 'source', label: 'Source', width: 120, type: 'text' },
  { id: 'expected_ctc', label: 'Expected CTC', width: 120, type: 'text' },
  { id: 'current_ctc', label: 'Current CTC', width: 120, type: 'text' },
  { id: 'notice_period', label: 'Notice Period', width: 120, type: 'text' },
  { id: 'linkedin', label: 'LinkedIn', width: 200, type: 'url' },
  { id: 'notes', label: 'Notes', width: 250, type: 'text' },
  { id: 'created_at', label: 'Added On', width: 120, type: 'date', readonly: true },
];

// Status color mapping
const STATUS_COLORS = {
  'Active': 'bg-green-100 text-green-700 border-green-200',
  'Inactive': 'bg-gray-100 text-gray-700 border-gray-200',
  'Hired': 'bg-blue-100 text-blue-700 border-blue-200',
  'Rejected': 'bg-red-100 text-red-700 border-red-200',
  'On Hold': 'bg-amber-100 text-amber-700 border-amber-200',
};

// Sheet color options
const SHEET_COLORS = [
  { id: 'emerald', value: '#10B981', label: 'Green' },
  { id: 'blue', value: '#3B82F6', label: 'Blue' },
  { id: 'violet', value: '#8B5CF6', label: 'Purple' },
  { id: 'rose', value: '#F43F5E', label: 'Red' },
  { id: 'amber', value: '#F59E0B', label: 'Orange' },
  { id: 'cyan', value: '#06B6D4', label: 'Cyan' },
  { id: 'pink', value: '#EC4899', label: 'Pink' },
  { id: 'slate', value: '#64748B', label: 'Gray' },
];

const CandidateSpreadsheet = ({ standalone = false }) => {
  // Sheets state
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [loadingSheets, setLoadingSheets] = useState(true);
  const [showNewSheet, setShowNewSheet] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [newSheetName, setNewSheetName] = useState('');
  const [newSheetColor, setNewSheetColor] = useState('#10B981');
  const [newSheetKind, setNewSheetKind] = useState('candidate');
  
  // Candidates state
  const [candidates, setCandidates] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]); // For adding to sheets
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.slice(0, 10).map(c => c.id)
  );
  const [showAddRow, setShowAddRow] = useState(false);
  const [showAddToSheet, setShowAddToSheet] = useState(false);
  const [newCandidate, setNewCandidate] = useState({});
  const [changedCells, setChangedCells] = useState(new Map());
  const [gridMode, setGridMode] = useState(true);
  const [sheetError, setSheetError] = useState('');
  const sheetLoadVersion = useRef(0);

  const inputRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    fetchSheets();
    fetchAllCandidates();
  }, []);

  useEffect(() => {
    if (activeSheet) {
      const url = new URL(window.location.href);
      url.searchParams.set('sheet', activeSheet.id);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      if (activeSheet.kind === 'blank') setCandidates([]);
      else fetchSheetCandidates(activeSheet.id);
      setSelectedRows(new Set());
    }
  }, [activeSheet?.id]);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  const fetchSheets = async () => {
    setLoadingSheets(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/spreadsheet-sheets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedSheets = response.data.sheets || [];
      setSheets(fetchedSheets);
      
      // Set active sheet to first one if not set
      if (fetchedSheets.length > 0) setActiveSheet(previous => fetchedSheets.find(sheet => sheet.id === (previous?.id || new URLSearchParams(window.location.search).get('sheet'))) || fetchedSheets[0]);
    } catch (error) {
      console.error('Error fetching sheets:', error);
      toast.error('Failed to load sheets');
    } finally {
      setLoadingSheets(false);
    }
  };

  const fetchAllCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/candidate-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllCandidates(response.data || []);
    } catch (error) {
      console.error('Error fetching all candidates:', error);
    }
  };

  const fetchSheetCandidates = async (sheetId, quiet = false) => {
    const version = ++sheetLoadVersion.current;
    setSheetError('');
    if (!quiet) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/spreadsheet-sheets/${sheetId}/candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (version === sheetLoadVersion.current) setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Error fetching sheet candidates:', error);
      if (version === sheetLoadVersion.current) setSheetError('Could not load this sheet. Your saved data has not changed.');
      toast.error('Failed to load candidates');
    } finally {
      if (version === sheetLoadVersion.current) setLoading(false);
    }
  };

  const handleCreateSheet = async () => {
    if (!newSheetName.trim()) {
      toast.error('Please enter a sheet name');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/spreadsheet-sheets`, {
        name: newSheetName,
        color: newSheetColor,
        kind: newSheetKind
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newSheet = response.data.sheet;
      setSheets(prev => [...prev, newSheet]);
      setActiveSheet(newSheet);
      setShowNewSheet(false);
      setNewSheetName('');
      setNewSheetColor('#10B981');
      setNewSheetKind('candidate');
      toast.success('Sheet created');
    } catch (error) {
      toast.error('Failed to create sheet');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSheet = async () => {
    if (!editingSheet || !newSheetName.trim()) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/spreadsheet-sheets/${editingSheet.id}`, {
        name: newSheetName,
        color: newSheetColor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSheets(prev => prev.map(s => 
        s.id === editingSheet.id ? { ...s, name: newSheetName, color: newSheetColor } : s
      ));
      
      if (activeSheet?.id === editingSheet.id) {
        setActiveSheet(prev => ({ ...prev, name: newSheetName, color: newSheetColor }));
      }
      
      setShowEditSheet(false);
      setEditingSheet(null);
      toast.success('Sheet updated');
    } catch (error) {
      toast.error('Failed to update sheet');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSheet = async (sheetId) => {
    const sheet = sheets.find(s => s.id === sheetId);
    if (sheet?.is_default) {
      toast.error('Cannot delete the default sheet');
      return;
    }

    if (!window.confirm('Delete this sheet? Candidates will not be deleted.')) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/spreadsheet-sheets/${sheetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSheets(prev => prev.filter(s => s.id !== sheetId));
      
      if (activeSheet?.id === sheetId) {
        setActiveSheet(sheets.find(s => s.id !== sheetId) || null);
      }
      
      toast.success('Sheet deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sheet');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCandidatesToSheet = async () => {
    if (!activeSheet || selectedRows.size === 0) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/spreadsheet-sheets/${activeSheet.id}/candidates`, {
        candidate_ids: Array.from(selectedRows)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchSheetCandidates(activeSheet.id);
      setSelectedRows(new Set());
      setShowAddToSheet(false);
      toast.success('Candidates added to sheet');
    } catch (error) {
      toast.error('Failed to add candidates');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFromSheet = async (candidateId) => {
    if (!activeSheet || activeSheet.is_default) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/spreadsheet-sheets/${activeSheet.id}/candidates/${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCandidates(prev => prev.filter(c => c.id !== candidateId));
      toast.success('Removed from sheet');
    } catch (error) {
      toast.error('Failed to remove candidate');
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  const getSortedCandidates = () => {
    let filtered = candidates.filter(c => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        c.candidate_name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.phone?.toLowerCase().includes(query) ||
        c.current_company?.toLowerCase().includes(query) ||
        c.skills?.toLowerCase().includes(query) ||
        c.current_location?.toLowerCase().includes(query)
      );
    });

    return filtered.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';
      
      if (sortColumn === 'created_at') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  const startEditing = (candidateId, columnId, value) => {
    const column = ALL_COLUMNS.find(c => c.id === columnId);
    if (column?.readonly) return;
    
    setEditingCell({ candidateId, columnId });
    setEditValue(value || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveCell = async () => {
    if (!editingCell) return;
    
    const { candidateId, columnId } = editingCell;
    const candidate = candidates.find(c => c.id === candidateId);
    
    if (!candidate || candidate[columnId] === editValue) {
      cancelEditing();
      return;
    }

    // Update local state immediately
    setCandidates(prev => prev.map(c => 
      c.id === candidateId ? { ...c, [columnId]: editValue } : c
    ));

    // Track changed cells
    const cellKey = `${candidateId}-${columnId}`;
    setChangedCells(prev => new Map(prev).set(cellKey, editValue));

    // Save to backend
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/candidate-status/${candidateId}`, {
        [columnId]: editValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from changed cells after successful save
      setChangedCells(prev => {
        const newMap = new Map(prev);
        newMap.delete(cellKey);
        return newMap;
      });
    } catch (error) {
      toast.error('Failed to save');
      // Revert on error
      setCandidates(prev => prev.map(c => 
        c.id === candidateId ? { ...c, [columnId]: candidate[columnId] } : c
      ));
    } finally {
      setSaving(false);
    }

    cancelEditing();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveCell();
    } else if (e.key === 'Escape') {
      cancelEditing();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      saveCell();
    }
  };

  const handleAddCandidate = async () => {
    if (!newCandidate.candidate_name || !newCandidate.email) {
      toast.error('Name and Email are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/candidate-status`, newCandidate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const createdCandidate = response.data;
      
      // Add to current sheet if not default
      if (activeSheet && !activeSheet.is_default) {
        await axios.post(`${API}/api/spreadsheet-sheets/${activeSheet.id}/candidates`, {
          candidate_ids: [createdCandidate.id]
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setCandidates(prev => [createdCandidate, ...prev]);
      setAllCandidates(prev => [createdCandidate, ...prev]);
      setNewCandidate({});
      setShowAddRow(false);
      toast.success('Candidate added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add candidate');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.size === 0) return;
    
    if (!window.confirm(`Delete ${selectedRows.size} candidate(s)?`)) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        Array.from(selectedRows).map(id =>
          axios.delete(`${API}/api/candidate-status/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      
      setCandidates(prev => prev.filter(c => !selectedRows.has(c.id)));
      setSelectedRows(new Set());
      toast.success(`Deleted ${selectedRows.size} candidate(s)`);
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(new Set(getSortedCandidates().map(c => c.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (candidateId, checked) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(candidateId);
      } else {
        newSet.delete(candidateId);
      }
      return newSet;
    });
  };

  const exportToCSV = () => {
    const exportColumns = ALL_COLUMNS.filter(column => visibleColumns.includes(column.id));
    const csv = Papa.unparse({ fields: exportColumns.map(column => column.label), data: getSortedCandidates().map(row => exportColumns.map(column => row[column.id] ?? '')) }, { escapeFormulae: true });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeSheet?.name || 'candidates'}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const sortedCandidates = getSortedCandidates();
  const visibleColumnDefs = ALL_COLUMNS.filter(c => visibleColumns.includes(c.id));
  const isBlank = activeSheet?.kind === 'blank';

  if (loadingSheets) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="candidate-spreadsheet">
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: activeSheet?.color || '#10B981' }}
            >
              <FileSpreadsheet className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">
                {activeSheet?.name || 'Candidate Spreadsheet'}
              </h1>
              <p className="text-sm text-slate-500">
                {isBlank ? `Blank sheet • your own columns • ${sheets.length} sheet(s)` : `${candidates.length} candidates • ${sheets.length} sheet(s)`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Button asChild variant="outline" size="sm" data-testid="sheets-open-new-tab">
              <a href={`/sheets${activeSheet?.id ? `?sheet=${activeSheet.id}` : ''}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" />Open in new tab
              </a>
            </Button>
            {!standalone && (
              <Button asChild variant="outline" size="sm" data-testid="sheets-open-fullscreen">
                <a href={`/sheets${activeSheet?.id ? `?sheet=${activeSheet.id}` : ''}`} title="Open the full-screen sheets workspace">
                  <Maximize2 className="h-4 w-4 mr-1" />Full screen
                </a>
              </Button>
            )}
            {!isBlank && <>
            {selectedRows.size > 0 && (
              <>
                {!activeSheet?.is_default && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      selectedRows.forEach(id => handleRemoveFromSheet(id));
                      setSelectedRows(new Set());
                    }}
                  >
                    Remove from Sheet
                  </Button>
                )}
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={saving}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete ({selectedRows.size})
                </Button>
              </>
            )}
            
            {!activeSheet?.is_default && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAddToSheet(true)}
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Add Existing
              </Button>
            )}
            
            <Button data-testid="sheets-export-csv" variant="outline" size="sm" onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
            <SheetCsvImport sheetId={activeSheet?.id} columns={ALL_COLUMNS} onSaved={() => fetchSheetCandidates(activeSheet.id)} />
            <Button data-testid="sheets-view-toggle" variant="outline" size="sm" onClick={() => setGridMode(!gridMode)}>{gridMode ? 'Table view' : 'Spreadsheet view'}</Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="sheets-columns-trigger" variant="outline" size="sm">
                  <Columns className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
                <DropdownMenuItem data-testid="sheets-columns-show-all" onSelect={() => setVisibleColumns(ALL_COLUMNS.map(column => column.id))}>Show all columns</DropdownMenuItem>
                {ALL_COLUMNS.map(col => (
                  <DropdownMenuCheckboxItem
                    key={col.id}
                    data-testid={`sheets-column-toggle-${col.id}`}
                    onSelect={event => event.preventDefault()}
                    checked={visibleColumns.includes(col.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setVisibleColumns(prev => [...prev, col.id]);
                      } else {
                        setVisibleColumns(prev => prev.filter(id => id !== col.id));
                      }
                    }}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => activeSheet && fetchSheetCandidates(activeSheet.id)}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            <Button 
              size="sm"
              onClick={() => setShowAddRow(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
            </>}
          </div>
        </div>
        
        {/* Search */}
        {!isBlank && (
        <div className="mt-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search candidates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        )}
      </div>

      {/* Spreadsheet Grid */}
      <div className="flex-1 min-w-0 overflow-auto" ref={tableRef}>
        {isBlank ? (
          <FreeSheetGrid key={activeSheet.id} sheet={activeSheet} standalone={standalone} />
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : sheetError ? (
          <div data-testid="sheet-load-error" role="alert" className="p-6 text-sm text-red-700">{sheetError}<Button data-testid="sheet-load-retry" variant="outline" onClick={() => fetchSheetCandidates(activeSheet.id)} className="ml-3">Retry</Button></div>
        ) : sortedCandidates.length === 0 ? (
          <div data-testid="sheet-empty-state" className="px-5 py-12 text-center border-t bg-gradient-to-b from-emerald-50/50 to-white">
            <FileSpreadsheet className="h-10 w-10 mx-auto mb-4 text-emerald-600" />
            <h2 data-testid="sheet-empty-title" className="text-lg font-semibold text-slate-800">{searchQuery ? 'No matching candidates' : `${activeSheet?.name || 'This sheet'} is ready`}</h2>
            <p className="text-sm text-slate-500 mt-2">{searchQuery ? 'Try a different search.' : 'No candidates added yet.'}</p>
            <div className="flex flex-wrap justify-center gap-2 mt-5">{searchQuery ? <Button data-testid="sheet-clear-search" variant="outline" onClick={() => setSearchQuery('')}>Clear search</Button> : <>
              <Button data-testid="sheet-empty-add-new" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setShowAddRow(true)}><Plus className="w-4 h-4 mr-2" />Add candidate</Button>
              {!activeSheet?.is_default && <Button data-testid="sheet-empty-add-existing" variant="outline" onClick={() => setShowAddToSheet(true)}><UserPlus className="w-4 h-4 mr-2" />Add existing</Button>}
            </>}</div>
          </div>
        ) : gridMode ? (
          <SpreadsheetGrid key={activeSheet?.id} sheetId={activeSheet?.id} rows={sortedCandidates} columns={visibleColumnDefs} selectedRows={selectedRows} onSelect={setSelectedRows} onSaved={() => fetchSheetCandidates(activeSheet.id, true)} />
        ) : (
          <div className="min-w-max">
            <table className="w-full border-collapse text-sm" data-testid="spreadsheet-table">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 border-b">
                  <th className="w-10 p-2 border-r bg-slate-200 sticky left-0 z-20">
                    <Checkbox
                      checked={selectedRows.size === sortedCandidates.length && sortedCandidates.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="w-12 p-2 border-r bg-slate-200 text-xs font-medium text-slate-500 sticky left-10 z-20">
                    #
                  </th>
                  {visibleColumnDefs.map(col => (
                    <th 
                      key={col.id}
                      style={{ minWidth: col.width }}
                      className="p-2 border-r bg-slate-100 text-left cursor-pointer hover:bg-slate-200 transition-colors"
                      onClick={() => handleSort(col.id)}
                    >
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-slate-700 truncate">{col.label}</span>
                        {sortColumn === col.id && (
                          sortDirection === 'asc' 
                            ? <ArrowUp className="h-3 w-3 text-emerald-600" />
                            : <ArrowDown className="h-3 w-3 text-emerald-600" />
                        )}
                      </div>
                    </th>
                  ))}
                  {!activeSheet?.is_default && (
                    <th className="w-16 p-2 border-r bg-slate-100 text-xs font-medium text-slate-500">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedCandidates.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnDefs.length + 3} className="text-center py-12 text-slate-500">
                      <User className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No candidates in this sheet</p>
                      <p className="text-sm mt-1">
                        {activeSheet?.is_default 
                          ? 'Add your first candidate' 
                          : 'Add existing candidates or create new ones'}
                      </p>
                      <div className="flex items-center justify-center gap-2 mt-4">
                        <Button 
                          onClick={() => setShowAddRow(true)} 
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add New
                        </Button>
                        {!activeSheet?.is_default && (
                          <Button 
                            variant="outline"
                            onClick={() => setShowAddToSheet(true)}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Add Existing
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedCandidates.map((candidate, rowIndex) => (
                    <tr 
                      key={candidate.id} 
                      className={`border-b hover:bg-slate-50 ${selectedRows.has(candidate.id) ? 'bg-emerald-50' : ''}`}
                    >
                      <td className="w-10 p-2 border-r bg-white sticky left-0">
                        <Checkbox
                          checked={selectedRows.has(candidate.id)}
                          onCheckedChange={(checked) => handleSelectRow(candidate.id, checked)}
                        />
                      </td>
                      <td className="w-12 p-2 border-r bg-slate-50 text-xs text-slate-500 text-center sticky left-10">
                        {rowIndex + 1}
                      </td>
                      {visibleColumnDefs.map(col => {
                        const isEditing = editingCell?.candidateId === candidate.id && editingCell?.columnId === col.id;
                        const cellKey = `${candidate.id}-${col.id}`;
                        const isChanged = changedCells.has(cellKey);
                        let value = candidate[col.id] || '';
                        
                        if (col.type === 'date' && value) {
                          try {
                            value = format(new Date(value), 'MMM d, yyyy');
                          } catch (e) {
                            value = value;
                          }
                        }
                        
                        return (
                          <td 
                            key={col.id}
                            style={{ minWidth: col.width }}
                            className={`p-0 border-r relative ${isEditing ? 'bg-emerald-50 ring-2 ring-emerald-500 ring-inset' : ''} ${isChanged ? 'bg-amber-50' : ''}`}
                            onDoubleClick={() => !col.readonly && startEditing(candidate.id, col.id, candidate[col.id])}
                          >
                            {isEditing ? (
                              <div className="flex items-center">
                                {col.type === 'select' ? (
                                  <select
                                    ref={inputRef}
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    onBlur={saveCell}
                                    className="w-full h-full p-2 text-sm border-0 outline-none bg-transparent"
                                  >
                                    <option value="">Select...</option>
                                    {col.options?.map(opt => (
                                      <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    ref={inputRef}
                                    type={col.type === 'email' ? 'email' : col.type === 'phone' ? 'tel' : 'text'}
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    onBlur={saveCell}
                                    className="w-full h-full p-2 text-sm border-0 outline-none bg-transparent"
                                  />
                                )}
                                {saving && <Loader2 className="h-3 w-3 animate-spin mr-1 text-emerald-500" />}
                              </div>
                            ) : (
                              <div 
                                className={`p-2 text-sm truncate cursor-cell min-h-[36px] ${col.readonly ? 'text-slate-400 cursor-default' : 'text-slate-700'}`}
                                title={value}
                              >
                                {col.id === 'status' && value ? (
                                  <Badge variant="outline" className={`text-xs ${STATUS_COLORS[value] || ''}`}>
                                    {value}
                                  </Badge>
                                ) : col.type === 'url' && value ? (
                                  <a 
                                    href={value.startsWith('http') ? value : `https://${value}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {value.replace(/^https?:\/\/(www\.)?/, '').slice(0, 25)}...
                                  </a>
                                ) : (
                                  value
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      {!activeSheet?.is_default && (
                        <td className="w-16 p-2 border-r text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromSheet(candidate.id)}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                            title="Remove from sheet"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sheet Tabs Footer */}
      <div className="border-t bg-slate-100 p-2">
        <div className="flex flex-wrap items-center gap-1 min-w-0">
          {sheets.map(sheet => (
            <div
              key={sheet.id}
              className={`group flex items-center gap-1 px-3 py-1.5 rounded-t-lg cursor-pointer transition-all ${
                activeSheet?.id === sheet.id 
                  ? 'bg-white border-t-2 shadow-sm' 
                  : 'bg-slate-200 hover:bg-slate-300'
              }`}
              style={{ borderTopColor: activeSheet?.id === sheet.id ? sheet.color : 'transparent' }}
              onClick={() => setActiveSheet(sheet)}
            >
              <div 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: sheet.color }}
              />
              <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                {sheet.name}
              </span>
              {!sheet.is_default && activeSheet?.id === sheet.id && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`sheet-tab-menu-${sheet.id}`} className="ml-1 p-0.5 rounded hover:bg-slate-200 transition-opacity">
                      <MoreHorizontal className="h-3 w-3 text-slate-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => {
                      setEditingSheet(sheet);
                      setNewSheetName(sheet.name);
                      setNewSheetColor(sheet.color);
                      setShowEditSheet(true);
                    }}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => handleDeleteSheet(sheet.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
          
          {/* Add New Sheet Button */}
          <button
            onClick={() => setShowNewSheet(true)}
            data-testid="sheets-create-new"
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 transition-colors max-w-full"
          >
            <PlusCircle className="h-4 w-4 text-slate-500" />
            <span className="text-sm text-slate-600">New Sheet</span>
          </button>
        </div>
        
        {/* Status bar */}
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500 px-1">
          <span>{isBlank ? 'Click a cell to edit • paste from Excel • use the column menu to rename, hide, move or delete' : 'Double-click to edit • Enter to save • Tab to move'}</span>
          <span>{isBlank ? `${activeSheet?.name} · blank sheet` : `${sortedCandidates.length} of ${candidates.length} candidates`}</span>
        </div>
      </div>

      {/* Create New Sheet Dialog */}
      <Dialog open={showNewSheet} onOpenChange={setShowNewSheet}>
        <DialogContent data-testid="sheets-create-dialog" className="w-[calc(100%-2rem)] sm:max-w-md [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5 text-emerald-500" />
              Create New Sheet
            </DialogTitle>
            <DialogDescription>
              Choose a candidate sheet or a blank sheet with your own columns
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Sheet type</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { id: 'candidate', title: 'Candidate sheet', note: 'Standard candidate columns' },
                  { id: 'blank', title: 'Blank sheet', note: 'Your own columns and rows' }
                ].map(option => (
                  <button
                    key={option.id}
                    type="button"
                    data-testid={`sheets-create-kind-${option.id}`}
                    onClick={() => setNewSheetKind(option.id)}
                    className={`text-left rounded-xl border p-3 transition-all ${newSheetKind === option.id ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <p className="text-sm font-semibold text-slate-800">{option.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{option.note}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Sheet Name</Label>
              <Input
                placeholder={newSheetKind === 'blank' ? 'e.g., Client Tracker' : 'e.g., Frontend Developers'}
                data-testid="sheets-create-name"
                value={newSheetName}
                onChange={(e) => setNewSheetName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SHEET_COLORS.map(color => (
                  <button
                    key={color.id}
                    data-testid={`sheets-create-color-${color.id}`}
                    onClick={() => setNewSheetColor(color.value)}
                    className={`w-8 h-8 rounded-full transition-all ${
                      newSheetColor === color.value ? 'ring-2 ring-offset-2 ring-slate-400' : ''
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button data-testid="sheets-create-cancel" variant="outline" onClick={() => {
              setShowNewSheet(false);
              setNewSheetName('');
              setNewSheetColor('#10B981');
              setNewSheetKind('candidate');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSheet}
              data-testid="sheets-create-submit"
              disabled={saving || !newSheetName.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Sheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sheet Dialog */}
      <Dialog open={showEditSheet} onOpenChange={setShowEditSheet}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-emerald-500" />
              Edit Sheet
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Sheet Name</Label>
              <Input
                placeholder="Sheet name"
                value={newSheetName}
                onChange={(e) => setNewSheetName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SHEET_COLORS.map(color => (
                  <button
                    key={color.id}
                    onClick={() => setNewSheetColor(color.value)}
                    className={`w-8 h-8 rounded-full transition-all ${
                      newSheetColor === color.value ? 'ring-2 ring-offset-2 ring-slate-400' : ''
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditSheet(false);
              setEditingSheet(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateSheet}
              disabled={saving || !newSheetName.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Candidate Dialog */}
      <Dialog open={showAddRow} onOpenChange={setShowAddRow}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-500" />
              Add New Candidate
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            {ALL_COLUMNS.filter(c => !c.readonly).slice(0, 12).map(col => (
              <div key={col.id} className={col.id === 'notes' ? 'col-span-2' : ''}>
                <Label>
                  {col.label} {col.required && <span className="text-red-500">*</span>}
                </Label>
                {col.type === 'select' ? (
                  <Select 
                    value={newCandidate[col.id] || ''} 
                    onValueChange={(v) => setNewCandidate(prev => ({ ...prev, [col.id]: v }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder={`Select ${col.label}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {col.options?.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={col.type === 'email' ? 'email' : col.type === 'phone' ? 'tel' : 'text'}
                    placeholder={`Enter ${col.label.toLowerCase()}`}
                    value={newCandidate[col.id] || ''}
                    onChange={(e) => setNewCandidate(prev => ({ ...prev, [col.id]: e.target.value }))}
                    className="mt-1.5"
                  />
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddRow(false);
              setNewCandidate({});
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCandidate}
              disabled={saving || !newCandidate.candidate_name || !newCandidate.email}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Existing Candidates to Sheet Dialog */}
      <Dialog open={showAddToSheet} onOpenChange={setShowAddToSheet}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-emerald-500" />
              Add Candidates to "{activeSheet?.name}"
            </DialogTitle>
            <DialogDescription>
              Select candidates to add to this sheet
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Input
              placeholder="Search candidates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-4"
            />
            
            <ScrollArea className="h-[400px] border rounded-lg">
              <div className="p-2">
                {allCandidates
                  .filter(c => !candidates.find(sc => sc.id === c.id))
                  .filter(c => {
                    if (!searchQuery) return true;
                    const q = searchQuery.toLowerCase();
                    return c.candidate_name?.toLowerCase().includes(q) || 
                           c.email?.toLowerCase().includes(q);
                  })
                  .map(candidate => (
                    <div
                      key={candidate.id}
                      className={`flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer ${
                        selectedRows.has(candidate.id) ? 'bg-emerald-50' : ''
                      }`}
                      onClick={() => handleSelectRow(candidate.id, !selectedRows.has(candidate.id))}
                    >
                      <Checkbox
                        checked={selectedRows.has(candidate.id)}
                        onCheckedChange={(checked) => handleSelectRow(candidate.id, checked)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{candidate.candidate_name}</p>
                        <p className="text-sm text-slate-500 truncate">{candidate.email}</p>
                      </div>
                      {candidate.status && (
                        <Badge variant="outline" className={`text-xs ${STATUS_COLORS[candidate.status] || ''}`}>
                          {candidate.status}
                        </Badge>
                      )}
                    </div>
                  ))
                }
                {allCandidates.filter(c => !candidates.find(sc => sc.id === c.id)).length === 0 && (
                  <p className="text-center text-slate-500 py-8">
                    All candidates are already in this sheet
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddToSheet(false);
              setSelectedRows(new Set());
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddCandidatesToSheet}
              disabled={saving || selectedRows.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add {selectedRows.size} Candidate(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateSpreadsheet;
